import { ExportExcelParseOption } from '@/appLogic/interfaces/exportExcel';
import XLSX, { CellAddress } from 'xlsx';
import XlsxPopulate from 'xlsx-populate';
import { ExcelOutputRow } from '@/coreDomainsModule/translateExcel';
import { ExcelExportResult } from '@/coreDomainsModule/exportSupplierForecastExcel';
import { IYearMonth } from '@/coreDomainsModule/date';
import {
  IAddColumnUseAmount,
  isColumnNamesOfExportFile,
  isSubTotalSupplierForecastExportData,
  ISubTotalSupplierForecastExportData,
  ISupplierForecastExportData,
  SupplierForecastExportData,
  ColumnNamesOfExportFile,
} from '../domains/entities/supplierForecastExportData';
import { SupplierForecastExportDataService } from '../domains/service/supplierForecastExportDataService';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { i18n } from '@/i18n';

const t = i18n.t;

/**
 * 列マッピング用インターフェース
 */
interface IColumnMapping {
  dataKey: keyof ISupplierForecastExportData; // データのキー
  colIndex: number; // 1-basedの列番号 (B列=2, C列=3, ...)
  headerLabelKey: string; // 翻訳キー
  headerRow: number; // ヘッダーを書く行
}

// Constants
const EMPTY_COLUMN_KEYS: (keyof ISupplierForecastExportData)[] = ['warehouseNumber', 'warehouse', 'memo1', 'memo2'];
const COLUMN_INDICES = {
  FIRST_DATA: 2,
  LAST_DATA: 10,
  FIRST_FORECAST: 11,
  PACKAGE: 10,
} as const;
const COLORS = {
  SUBTOTAL: 'FFF2CC',
  GRAND_TOTAL: 'FCE4D6',
  HEADER: 'FFC000',
} as const;
const ROWS = {
  YEAR_HEADER: 1,
  MONTH_HEADER: 2,
  DATA_START: 3,
} as const;
const STYLES = {
  COLUMN_WIDTH: 20,
  GRAND_TOTAL_HEIGHT: 66,
} as const;

// Translation constants
const TRANSLATION_KEYS = {
  PRODUCT_CATEGORY: 'Product Category',
} as const;

/**
 * Cell style information interface
 */
interface ICellStyles {
  fill?: any;
  border?: any;
  bold?: boolean;
  horizontalAlignment?: string;
  fontSize?: number;
  fontColor?: string;
}

/**
 * Border style configuration
 */
interface IBorderStyle {
  top?: { style: string; color?: string } | string;
  bottom?: { style: string; color?: string } | string;
  left?: { style: string; color?: string } | string;
  right?: { style: string; color?: string } | string;
}

/**
 * 仕入先内示のダウンロードを行うユースケース
 */
export class DownloadForecastUseCase {
  private static readonly t = i18n.t;

  /**
   * Create a border style object
   */
  private static createBorderStyle(options: { top?: string; bottom?: string; left?: string; right?: string }): IBorderStyle {
    return {
      top: options.top ? { style: options.top } : { style: 'none' },
      bottom: options.bottom ? { style: options.bottom } : { style: 'none' },
      left: options.left ? { style: options.left } : { style: 'none' },
      right: options.right ? { style: options.right } : { style: 'none' },
    };
  }

  /**
   * Apply border style to a cell
   * @param cell - The cell to apply border style to
   * @param borderStyle - The border style configuration
   */
  private static applyBorder(cell: any, borderStyle: IBorderStyle): void {
    cell.style('border', borderStyle);
  }

  /**
   * Apply fill color to a cell
   * @param cell - The cell to apply fill color to
   * @param color - The fill color (hex string)
   */
  private static applyFill(cell: any, color: string): void {
    cell.style('fill', { type: 'solid', color });
  }

  /**
   * Style a range of columns with borders
   * @param sheet - The worksheet to style
   * @param row - The row number to style
   * @param startCol - The starting column index
   * @param endCol - The ending column index
   * @param options - Style options including fill color, border style, and bold
   */
  private static styleColumnRange(
    sheet: any,
    row: number,
    startCol: number,
    endCol: number,
    options: {
      fillColor?: string;
      borderStyle: IBorderStyle;
      bold?: boolean;
    }
  ): void {
    for (let col = startCol; col <= endCol; col++) {
      const cell = sheet.cell(row, col);
      if (options.fillColor) {
        this.applyFill(cell, options.fillColor);
      }
      this.applyBorder(cell, options.borderStyle);
      if (options.bold !== undefined) {
        cell.style('bold', options.bold);
      }
    }
  }

  /**
   * Write forecast values for a row
   * @param sheet - The worksheet to write to
   * @param row - The row number to write to
   * @param business - The business data (regular or subtotal)
   * @param additionalColumnsUseAmount - Array of additional column information
   * @param options - Style options including fill color, border style, bold, and isSubtotal flag
   */
  private static writeForecastValues(
    sheet: any,
    row: number,
    business: SupplierForecastExportData | ISubTotalSupplierForecastExportData,
    additionalColumnsUseAmount: IAddColumnUseAmount[],
    options: {
      fillColor?: string;
      borderStyle: IBorderStyle;
      bold: boolean;
      isSubtotal: boolean;
    }
  ): void {
    let forecastCol = COLUMN_INDICES.FIRST_FORECAST;
    for (const addColumnInfo of additionalColumnsUseAmount) {
      const columnName = this.getColumnName(addColumnInfo.type);
      const value = options.isSubtotal
        ? SupplierForecastExportDataService.getExportValueSubTotal(
            business as ISubTotalSupplierForecastExportData,
            columnName,
            addColumnInfo.yearMonth
          )
        : SupplierForecastExportDataService.getExportValue(business as ISupplierForecastExportData, columnName, addColumnInfo.yearMonth);

      const cell = sheet.cell(row, forecastCol);
      cell.value(value);
      if (options.fillColor) {
        this.applyFill(cell, options.fillColor);
      }
      this.applyBorder(cell, options.borderStyle);
      cell.style('bold', options.bold);
      forecastCol++;
    }
  }

  /**
   * Helper function to preserve cell styles
   * @param cell - The cell to apply styles to
   * @param originalStyles - The original styles to preserve
   */
  private static preserveCellStyles(cell: any, originalStyles: ICellStyles): void {
    if (originalStyles.fill) cell.style('fill', originalStyles.fill);
    if (originalStyles.border) cell.style('border', originalStyles.border);
    if (originalStyles.bold !== undefined) cell.style('bold', originalStyles.bold);
    if (originalStyles.horizontalAlignment) cell.style('horizontalAlignment', originalStyles.horizontalAlignment);
    if (originalStyles.fontSize) cell.style('fontSize', originalStyles.fontSize);
    if (originalStyles.fontColor) cell.style('fontColor', originalStyles.fontColor);
  }

  /**
   * Get original styles from a cell
   * @param cell - The cell to get styles from
   * @returns Object containing all cell styles
   */
  private static getCellStyles(cell: any): ICellStyles {
    return {
      fill: cell.style('fill'),
      border: cell.style('border'),
      bold: cell.style('bold'),
      horizontalAlignment: cell.style('horizontalAlignment'),
      fontSize: cell.style('fontSize'),
      fontColor: cell.style('fontColor'),
    };
  }

  /**
   * Translate a cell value with multiple fallback strategies
   * @param valueStr - The string value to translate
   * @returns Translated string
   */
  private static translateValue(valueStr: string): string {
    // Try direct translation first
    let translated = DownloadForecastUseCase.t(valueStr);

    // If translation didn't change the value, try alternative approaches
    if (translated === valueStr) {
      // Map common Japanese header terms to their translations
      const headerTranslations: Record<string, string> = {
        メモ1: DownloadForecastUseCase.t('メモ') + '1',
        メモ2: DownloadForecastUseCase.t('メモ') + '2',
        memo1: DownloadForecastUseCase.t('メモ') + '1',
        memo2: DownloadForecastUseCase.t('メモ') + '2',
      };

      if (headerTranslations[valueStr]) {
        translated = headerTranslations[valueStr];
      } else if (valueStr.includes('メモ') || valueStr.toLowerCase().includes('memo')) {
        // Common patterns for memo columns
        const memoBase = DownloadForecastUseCase.t('メモ');
        if (valueStr.includes('1') || valueStr.toLowerCase().includes('1')) {
          translated = memoBase + '1';
        } else if (valueStr.includes('2') || valueStr.toLowerCase().includes('2')) {
          translated = memoBase + '2';
        } else {
          translated = memoBase;
        }
      } else {
        // Try translating word by word for compound headers
        const words = valueStr.split(/\s+/);
        const translatedWords = words.map((word) => {
          const wordTranslated = DownloadForecastUseCase.t(word);
          return wordTranslated !== word ? wordTranslated : word;
        });
        const finalTranslation = translatedWords.join(' ');
        if (finalTranslation !== valueStr) {
          translated = finalTranslation;
        } else {
          // Try splitting and translating parts
          // Check if it contains common Japanese header terms
          const headerTerms = ['選択', '展開', 'ID', '取引', '商品', '仕入先', '置場', '容量'];
          for (const term of headerTerms) {
            if (valueStr.includes(term)) {
              const termTranslated = DownloadForecastUseCase.t(term);
              if (termTranslated !== term) {
                translated = valueStr.replace(term, termTranslated);
                break;
              }
            }
          }
        }
      }
    }

    return translated;
  }

  /**
   * Get fill color from template or use default
   * @param sheet - The worksheet to get fill color from
   * @param row - The row number to check
   * @returns The fill color from template or undefined
   */
  private static getFillColorFromTemplate(sheet: any, row: number): any {
    const bCell = sheet.cell(row, COLUMN_INDICES.FIRST_DATA);
    let fillColor = bCell.style('fill');
    if (!fillColor) {
      const templateBReference = sheet.cell(ROWS.DATA_START, COLUMN_INDICES.FIRST_DATA);
      fillColor = templateBReference.style('fill');
    }
    return fillColor;
  }

  /**
   * Write subtotal row
   * @param sheet - The worksheet to write to
   * @param row - The row number to write to
   * @param business - The subtotal business data
   * @param additionalColumnsUseAmount - Array of additional column information
   */
  private static writeSubtotalRow(
    sheet: any,
    row: number,
    business: ISubTotalSupplierForecastExportData,
    additionalColumnsUseAmount: IAddColumnUseAmount[]
  ): void {
    const isEnglish = i18n.locale === 'en';

    // Write values using column mappings
    this.columnMappings.forEach((mapping) => {
      let value: string | number = '';
      if (mapping.dataKey === 'package') {
        const subtotalValue = this.t('合計');
        value = isEnglish && subtotalValue === '合計' ? 'subtotal' : subtotalValue;
      } else if (EMPTY_COLUMN_KEYS.includes(mapping.dataKey)) {
        value = '';
      } else {
        value = business[mapping.dataKey] || '';
      }
      sheet.cell(row, mapping.colIndex).value(value);
    });

    const fillColor = COLORS.SUBTOTAL;
    const subtotalBorderStyle = this.createBorderStyle({
      top: 'none',
      bottom: 'thin',
      left: 'thin',
      right: 'thin',
    });

    // Style column A
    const aCell = sheet.cell(row, 1);
    this.applyFill(aCell, fillColor);
    this.applyBorder(aCell, subtotalBorderStyle);

    // Style columns B-J
    this.styleColumnRange(sheet, row, COLUMN_INDICES.FIRST_DATA, COLUMN_INDICES.LAST_DATA, {
      fillColor,
      borderStyle: subtotalBorderStyle,
    });

    // Column J should be bold for subtotal rows
    sheet.cell(row, COLUMN_INDICES.PACKAGE).style('bold', true);

    // Write forecast values
    this.writeForecastValues(sheet, row, business, additionalColumnsUseAmount, {
      fillColor,
      borderStyle: subtotalBorderStyle,
      bold: true,
      isSubtotal: true,
    });
  }

  /**
   * Write regular data row
   * @param sheet - The worksheet to write to
   * @param row - The row number to write to
   * @param business - The regular business data
   * @param additionalColumnsUseAmount - Array of additional column information
   */
  private static writeRegularDataRow(
    sheet: any,
    row: number,
    business: ISupplierForecastExportData,
    additionalColumnsUseAmount: IAddColumnUseAmount[]
  ): void {
    // Write values using column mappings
    this.columnMappings.forEach((mapping) => {
      const value = business[mapping.dataKey] ?? '';
      sheet.cell(row, mapping.colIndex).value(value);
    });

    const fillColor = this.getFillColorFromTemplate(sheet, row);
    const regularBorderStyle = this.createBorderStyle({
      top: 'thin',
      bottom: 'none',
      left: 'thin',
      right: 'thin',
    });

    // Style column A
    const aCell = sheet.cell(row, 1);
    if (fillColor) {
      aCell.style('fill', fillColor);
    }
    this.applyBorder(aCell, regularBorderStyle);

    // Style columns B-J
    this.styleColumnRange(sheet, row, COLUMN_INDICES.FIRST_DATA, COLUMN_INDICES.LAST_DATA, {
      borderStyle: regularBorderStyle,
    });

    // Column J should be not bold for regular data rows
    sheet.cell(row, COLUMN_INDICES.PACKAGE).style('bold', false);

    // Write forecast values
    this.writeForecastValues(sheet, row, business, additionalColumnsUseAmount, {
      borderStyle: regularBorderStyle,
      bold: true,
      isSubtotal: false,
    });
  }

  /**
   * Write data rows (regular and subtotal rows)
   * @param sheet - The worksheet to write data in
   * @param businesses - Array of business data
   * @param additionalColumnsUseAmount - Array of additional column information
   * @returns The next available row number after all data rows
   */
  private static writeDataRows(
    sheet: any,
    businesses: SupplierForecastExportData[],
    additionalColumnsUseAmount: IAddColumnUseAmount[]
  ): number {
    let currentRow = ROWS.DATA_START;

    for (const business of businesses) {
      if (isSubTotalSupplierForecastExportData(business)) {
        this.writeSubtotalRow(sheet, currentRow, business, additionalColumnsUseAmount);
      } else {
        this.writeRegularDataRow(sheet, currentRow, business, additionalColumnsUseAmount);
      }
      currentRow++;
    }

    return currentRow;
  }

  /**
   * Write grand total row
   * @param sheet - The worksheet to write the grand total row in
   * @param grandTotalRow - The row number for the grand total
   * @param businesses - Array of business data
   * @param additionalColumnsUseAmount - Array of additional column information
   */
  private static writeGrandTotalRow(
    sheet: any,
    grandTotalRow: number,
    businesses: SupplierForecastExportData[],
    additionalColumnsUseAmount: IAddColumnUseAmount[]
  ): void {
    const isEnglish = i18n.locale === 'en';

    // Write values for grand total row
    DownloadForecastUseCase.columnMappings.forEach((mapping) => {
      let value: string | number = '';
      if (mapping.dataKey === 'package') {
        const grandTotalValue = SupplierForecastExportDataService.getExportValueAllTotal(businesses, '容量');
        value = isEnglish && grandTotalValue === '総計' ? 'total' : grandTotalValue;
      } else if (EMPTY_COLUMN_KEYS.includes(mapping.dataKey)) {
        value = '';
      }
      // Other columns (supplierNumber, supplierName, businessNumber, businessName) are left empty
      sheet.cell(grandTotalRow, mapping.colIndex).value(value);
    });

    // Don't apply styles to columns 1-9 (A-I) in grand total row
    // Only style column 10 (J, package/total column) and forecast columns
    const baseBorderStyle = {
      top: { style: 'medium' as const, color: '000000' },
      bottom: { style: 'medium' as const, color: '000000' },
      left: { style: 'thin' as const },
      right: { style: 'thin' as const },
    };

    // Style only column 10 (J, package/total column)
    const packageCell = sheet.cell(grandTotalRow, COLUMN_INDICES.PACKAGE);
    packageCell.style('fill', { type: 'solid', color: COLORS.GRAND_TOTAL });
    packageCell.style('bold', true);
    packageCell.style('border', {
      ...baseBorderStyle,
      left: { style: 'medium', color: '000000' },
      right: { style: 'medium', color: '000000' },
    });

    // Write grand total forecast values (columns K onwards)
    let forecastCol = COLUMN_INDICES.FIRST_FORECAST;
    const lastForecastCol = COLUMN_INDICES.FIRST_FORECAST + additionalColumnsUseAmount.length - 1;
    for (const addColumnInfo of additionalColumnsUseAmount) {
      const columnName = DownloadForecastUseCase.getColumnName(addColumnInfo.type);
      const value = SupplierForecastExportDataService.getExportValueAllTotal(businesses, columnName, addColumnInfo.yearMonth);
      const cell = sheet.cell(grandTotalRow, forecastCol);
      cell.value(value);
      cell.style('fill', { type: 'solid', color: COLORS.GRAND_TOTAL });
      cell.style('bold', true);
      cell.style('border', {
        ...baseBorderStyle,
        right: forecastCol === lastForecastCol ? { style: 'medium', color: '000000' } : { style: 'thin' },
      });
      forecastCol++;
    }

    // Set row height for grand total
    sheet.row(grandTotalRow).height(STYLES.GRAND_TOTAL_HEIGHT);
  }

  /**
   * Get header styles from reference cells with fallbacks
   * @param sheet - The worksheet to get header styles from
   * @returns Object containing year and month header styles
   */
  private static getHeaderStyles(sheet: any): {
    year: ICellStyles;
    month: ICellStyles;
  } {
    const referenceYearCell = sheet.cell(ROWS.YEAR_HEADER, 2);
    const referenceMonthCell = sheet.cell(ROWS.MONTH_HEADER, 2);

    /**
     * Helper function to get style with fallback
     * @param yearValue - Value from year header cell
     * @param monthValue - Value from month header cell
     * @param fallback - Fallback value if both are empty
     * @returns The style value
     */
    const getStyle = (yearValue: any, monthValue: any, fallback: any) => yearValue || monthValue || fallback;

    return {
      year: {
        fill: getStyle(referenceYearCell.style('fill'), referenceMonthCell.style('fill'), {
          type: 'solid',
          color: COLORS.HEADER,
        }),
        bold: referenceYearCell.style('bold') ?? referenceMonthCell.style('bold') ?? true,
        horizontalAlignment: referenceYearCell.style('horizontalAlignment') || referenceMonthCell.style('horizontalAlignment') || 'center',
        fontSize: referenceYearCell.style('fontSize') || referenceMonthCell.style('fontSize'),
        fontColor: referenceYearCell.style('fontColor') || referenceMonthCell.style('fontColor'),
      },
      month: {
        fill: getStyle(referenceMonthCell.style('fill'), referenceYearCell.style('fill'), {
          type: 'solid',
          color: COLORS.HEADER,
        }),
        border: getStyle(referenceMonthCell.style('border'), referenceYearCell.style('border'), {
          style: 'thin',
        }),
        bold: referenceMonthCell.style('bold') ?? referenceYearCell.style('bold') ?? true,
        horizontalAlignment: referenceMonthCell.style('horizontalAlignment') || referenceYearCell.style('horizontalAlignment') || 'center',
        fontSize: referenceMonthCell.style('fontSize') || referenceYearCell.style('fontSize'),
        fontColor: referenceMonthCell.style('fontColor') || referenceYearCell.style('fontColor'),
      },
    };
  }

  /**
   * Apply header styles to a cell
   * @param cell - The cell to apply styles to
   * @param styles - The cell styles to apply
   * @param borderStyle - Optional border style to apply (overrides styles.border if provided)
   */
  private static applyHeaderStyles(cell: any, styles: ICellStyles, borderStyle?: IBorderStyle): void {
    if (styles.fill) cell.style('fill', styles.fill);
    if (borderStyle) {
      this.applyBorder(cell, borderStyle);
    } else if (styles.border) {
      cell.style('border', styles.border);
    }
    if (styles.bold !== undefined) cell.style('bold', styles.bold);
    if (styles.horizontalAlignment) cell.style('horizontalAlignment', styles.horizontalAlignment);
    if (styles.fontSize) cell.style('fontSize', styles.fontSize);
    if (styles.fontColor) cell.style('fontColor', styles.fontColor);
  }

  /**
   * Write month headers for forecast columns
   * @param sheet - The worksheet to write headers in
   * @param additionalColumnsUseAmount - Array of additional column information
   */
  private static writeMonthHeaders(sheet: any, additionalColumnsUseAmount: IAddColumnUseAmount[]): void {
    const headerStyles = this.getHeaderStyles(sheet);
    const yearBorderStyle = this.createBorderStyle({
      top: 'thin',
      bottom: 'thin',
      left: 'thin',
      right: 'thin',
    });

    let currentCol = COLUMN_INDICES.FIRST_FORECAST;

    for (const addColumnInfo of additionalColumnsUseAmount) {
      const yearMonth = addColumnInfo.yearMonth;

      // Year header (row 1)
      const yearCell = sheet.cell(ROWS.YEAR_HEADER, currentCol);
      yearCell.value(yearMonth.year);
      this.applyHeaderStyles(yearCell, headerStyles.year, yearBorderStyle);
      yearCell.style('bold', true); // Explicitly set bold

      // Month header (row 2)
      const monthName = String(yearMonth.month) + t('月');
      const columnType = SupplierForecastExportDataService.getAddingColumnName(addColumnInfo.type);
      const monthCell = sheet.cell(ROWS.MONTH_HEADER, currentCol);
      monthCell.value(monthName + columnType);
      this.applyHeaderStyles(monthCell, headerStyles.month);

      sheet.column(currentCol).width(STYLES.COLUMN_WIDTH);
      currentCol++;
    }
  }

  /**
   * Translate headers in A1:J2 for English mode
   * @param sheet - The worksheet to translate headers in
   */
  private static translateHeaders(sheet: any): void {
    /**
     * Helper function to translate a cell while preserving styles
     * @param row - Row number (1-based)
     * @param col - Column number (1-based)
     */
    const translateCell = (row: number, col: number) => {
      const cell = sheet.cell(row, col);
      const currentValue = cell.value();

      // Only translate if there's a text value
      if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
        const valueStr = String(currentValue).trim();

        // Skip if already empty or whitespace
        if (!valueStr) return;

        // Get original styles before updating
        const originalStyles = DownloadForecastUseCase.getCellStyles(cell);

        // Translate the value
        const translatedValue = DownloadForecastUseCase.translateValue(valueStr);

        // Update the cell value
        cell.value(translatedValue);

        // Restore original styles (preserve color and style)
        DownloadForecastUseCase.preserveCellStyles(cell, originalStyles);
      }
    };

    // Translate row 1 headers (columns A-J = 1-10)
    for (let col = 1; col <= 10; col++) {
      translateCell(1, col);
    }

    // Translate row 2 headers
    // First, translate A2 (column 1, row 2) which is not in columnMappings
    translateCell(2, 1);

    // Then translate row 2 headers using columnMappings (columns B-J = 2-10)
    DownloadForecastUseCase.columnMappings.forEach((mapping) => {
      const cell = sheet.cell(mapping.headerRow, mapping.colIndex);
      // Get original styles before updating
      const originalStyles = DownloadForecastUseCase.getCellStyles(cell);

      // Translate the header text
      const translatedValue = DownloadForecastUseCase.t(mapping.headerLabelKey);
      cell.value(translatedValue);

      // Restore original styles (preserve color and style)
      DownloadForecastUseCase.preserveCellStyles(cell, originalStyles);
    });

    // Final explicit pass for A2, H1, and I1 to ensure they're translated
    translateCell(2, 1); // A2
    translateCell(1, 8); // H1
    translateCell(1, 9); // I1

    // Extra aggressive translation for A2 - translate to "Product Category"
    const a2Cell = sheet.cell(2, 1);
    const a2Value = a2Cell.value();
    if (a2Value !== null && a2Value !== undefined && a2Value !== '') {
      const a2ValueStr = String(a2Value).trim();
      if (a2ValueStr) {
        // Get original styles
        const originalStyles = DownloadForecastUseCase.getCellStyles(a2Cell);

        // Translate A2 to "Product Category"
        const a2Translated = TRANSLATION_KEYS.PRODUCT_CATEGORY;

        // Force update A2
        a2Cell.value(a2Translated);

        // Restore styles
        DownloadForecastUseCase.preserveCellStyles(a2Cell, originalStyles);
      }
    }
  }

  /**
   * 固定列のマッピング定義 (B列～J列)
   */
  private static readonly columnMappings: IColumnMapping[] = [
    { dataKey: 'supplierNumber', colIndex: 2, headerLabelKey: '仕入先CD', headerRow: 2 }, // B2
    { dataKey: 'supplierName', colIndex: 3, headerLabelKey: '仕入先名', headerRow: 2 }, // C2
    { dataKey: 'businessNumber', colIndex: 4, headerLabelKey: '商品CD', headerRow: 2 }, // D2
    { dataKey: 'businessName', colIndex: 5, headerLabelKey: '商品名', headerRow: 2 }, // E2
    { dataKey: 'warehouseNumber', colIndex: 6, headerLabelKey: '置場CD', headerRow: 2 }, // F2
    { dataKey: 'warehouse', colIndex: 7, headerLabelKey: '置場名', headerRow: 2 }, // G2
    { dataKey: 'memo1', colIndex: 8, headerLabelKey: 'memo1', headerRow: 2 }, // H2
    { dataKey: 'memo2', colIndex: 9, headerLabelKey: 'memo2', headerRow: 2 }, // I2
    { dataKey: 'package', colIndex: 10, headerLabelKey: '容量', headerRow: 2 }, // J2
  ];
  /**
   * 出力エクセルの作成を行う (仕入先内示)
   * @param file - ファイル情報
   * @param businesses - 対象取引 IBusinessForSupplierForecast[]
   * @param startYearMonth - 開始年月
   * @param endYearMonth - 終了年月
   */
  public static createSupplierAnnounceExcelForExport = async (
    file: Blob,
    businesses: SupplierForecastExportData[],
    startYearMonth: IYearMonth,
    endYearMonth: IYearMonth
  ): Promise<INetworkResult<Blob, Error>> => {
    if (!file) {
      return {
        status: {
          type: 'error',
          error: new Error('ファイルが存在しません'),
        },
      };
    }

    try {
      // Load workbook from template
      const workbook = await XlsxPopulate.fromDataAsync(file);

      // Get the first sheet (or use specific sheet name if needed)
      const sheet = workbook.sheet(0);

      // Check current locale
      const isEnglish = i18n.locale === 'en';

      if (isEnglish) {
        // English mode: Translate headers in A1:J2 (rows 1-2, columns 1-10)
        // Preserve all styles and colors, only update the text values
        DownloadForecastUseCase.translateHeaders(sheet);
      }
      // Japanese mode: Don't touch headers (A1:J2) at all

      // Calculate dynamic columns for forecasts (starting from column K = 11)
      const additionalColumnsUseAmount = SupplierForecastExportDataService.getAddingColumnsUseAmount(startYearMonth, endYearMonth);

      // Write month headers for forecast columns
      DownloadForecastUseCase.writeMonthHeaders(sheet, additionalColumnsUseAmount);

      // Write data rows (regular and subtotal rows)
      const grandTotalRow = DownloadForecastUseCase.writeDataRows(sheet, businesses, additionalColumnsUseAmount);

      // Write grand total row
      DownloadForecastUseCase.writeGrandTotalRow(sheet, grandTotalRow, businesses, additionalColumnsUseAmount);

      // Output file
      const wbout = await workbook.outputAsync();
      const blob = new Blob([wbout], { type: 'application/octet-stream' });

      return {
        status: {
          type: 'success',
          data: blob,
        },
      };
    } catch (error) {
      return {
        status: {
          type: 'error',
          error: error instanceof Error ? error : new Error('エクセル生成に失敗しました'),
        },
      };
    }
  };

  /**
   * 取引の各種情報から出力エクセルに必要な情報を取り出して出力用のオブジェクトにする
   * @param excelData - エクセルのデータ
   * @param businesses - 取引ID IBusinessForSupplierForecast[]
   * @param workSheetInfo - worksheet情報
   * @returns どこのセルに何の値を入れるか
   */
  public static splitExportExcel = async (
    excelData: ExcelOutputRow[],
    businesses: SupplierForecastExportData[],
    workSheetInfo: any // TODO: typesどっかから持って来れないか検討する
  ): Promise<ExcelExportResult | undefined> => {
    if (excelData.length === 0 || excelData[0] === undefined) return undefined;
    const output: ExcelExportResult = {};
    const excelRowInfo = excelData[0];
    businesses.forEach((business, index) => {
      this.createExportSupplierForecastInfo(business, output, excelRowInfo, index, workSheetInfo);
    });
    return output;
  };

  /**
   * 出力用エクセルファイルの列名更新、スタイル更新を行う
   * 出力する期間
   * @param workSheetInfo - worksheet情報
   * @param option - エクセルを読むためのオプションをまとめたもの
   * @param additionalColumnInfos - 追加列の情報
   */
  static updateExportExcelColumnName = (
    workSheetInfo: any,
    option: ExportExcelParseOption,
    additionalColumnInfos: IAddColumnUseAmount[]
  ): any => {
    // 更新開始セル(年月データが入っているセル)のアドレスを取る
    const startAddress = XLSX.utils.decode_cell(option.separateYmd.startCell);

    // 年月データが入っている列(内示・販売実績数量がある列)を対象に列名を決定する
    for (const [index, addColumnInfo] of additionalColumnInfos.entries()) {
      // 1行目の年月データが入っているセルのアドレスを取得
      const yearMonth = addColumnInfo.yearMonth;
      const yearAdr = XLSX.utils.encode_cell({ c: startAddress.c + index, r: startAddress.r }); // 年データが入っているセル
      const colNameAdr = XLSX.utils.encode_cell({ c: startAddress.c + index, r: startAddress.r + 1 }); // 列名が入っているセル

      const startCellColor = COLORS.HEADER;

      // 年情報の更新
      workSheetInfo.cell(yearAdr).value(yearMonth.year);
      workSheetInfo.cell(yearAdr).style('fill', startCellColor);
      workSheetInfo.cell(yearAdr).style('border', true);
      workSheetInfo.cell(yearAdr).style('bold', true);
      workSheetInfo.cell(yearAdr).style('horizontalAlignment', 'center');

      // 月情報の取得
      const updateMonthName = String(yearMonth.month) + t('月');

      // 月情報の更新
      // 新たな列を生成する必要があるため、空セルを読んだ際の対応分けをしている
      if (workSheetInfo.cell(colNameAdr).value()) {
        if (workSheetInfo.cell(colNameAdr).value().includes('月')) {
          const updateColName = updateMonthName + workSheetInfo.cell(colNameAdr).value().split('月')[1];
          workSheetInfo.cell(colNameAdr).value(updateColName);
        }
      } else {
        const addColName = updateMonthName + SupplierForecastExportDataService.getAddingColumnName(addColumnInfo.type);
        workSheetInfo.cell(colNameAdr).value(addColName);
        workSheetInfo.cell(colNameAdr).style('fill', startCellColor);
        workSheetInfo.cell(colNameAdr).style('border', true);
        workSheetInfo.cell(colNameAdr).style('horizontalAlignment', 'center');
        workSheetInfo.column(startAddress.c + index + 1).width(STYLES.COLUMN_WIDTH);
      }
    }
    return workSheetInfo;
  };

  /**
   * エクセル出力を行うために、必要な情報を持つオブジェクトの作成を行う
   * @param business - 取引情報 IBusinessForSupplierForecast
   * @param output - エクセル出力用のオブジェクト
   * @param rowInfo - 入力されたエクセルの行ごとの情報
   * @param addRowCount - 追加される行数
   * @param workSheetInfo - worksheetの情報
   */
  static createExportSupplierForecastInfo = (
    business: SupplierForecastExportData,
    output: ExcelExportResult,
    rowInfo: ExcelOutputRow,
    addRowCount: number,
    workSheetInfo: any // TODO: typesどっかから持って来れないか検討する
  ) => {
    // 列ごとに見ていく
    Object.keys(rowInfo).forEach((key) => {
      // 一応、セル番号が文字列かを見る
      if (typeof rowInfo[key].address === 'string') {
        // セル番号をコードで扱える形に変換する
        const cellAddress = XLSX.utils.decode_cell(rowInfo[key].address as string);
        // 取引の数分、下に行を進める
        const writeToAddress = XLSX.utils.encode_cell({ c: cellAddress.c, r: cellAddress.r + addRowCount });
        const cellNumber = String(writeToAddress); // セル番号
        // セルのstyle情報の取得を行う
        // エクセル番号、セルに入れる値を持つオブジェクトの初期化
        if (!output[writeToAddress]) {
          output[writeToAddress] = {
            value: undefined,
            address: undefined,
            colorInfo: undefined,
            borderInfo: undefined,
          };
        }

        // 出力先セル番号の取得
        const propertyValue = !isSubTotalSupplierForecastExportData(business)
          ? this.createSupplierForecastValueFetchFromProperty(business, key)
          : this.createSubTotalValueFetchFromProperty(business, key);
        // 出力情報の登録
        output[cellNumber].address = writeToAddress;
        output[cellNumber].value = propertyValue;
        // style情報の登録を行う
        if (isSubTotalSupplierForecastExportData(business)) {
          output[cellNumber].colorInfo = COLORS.SUBTOTAL;
          output[cellNumber].borderInfo = { left: true, right: true, bottom: true };
        } else {
          output[cellNumber].borderInfo = { left: true, right: true };
        }
        // 太字の条件: 内示or販売実績数量の列 or 合計行の容量（「小計」と表示）の列
        output[cellNumber].boldInfo =
          this.isAnnounceOrSalesResultProperty(key) ||
          (isSubTotalSupplierForecastExportData(business) && isColumnNamesOfExportFile(key) && key === '容量');
      }
    });
  };

  /**
   * 取引の各種情報から出力エクセルに必要な情報を取り出す
   * @param business - 取引情報 IBusinessForSupplierForecast
   * @param columnName - 列名
   */
  static createSupplierForecastValueFetchFromProperty = (business: ISupplierForecastExportData, columnName: string): string | number => {
    if (isColumnNamesOfExportFile(columnName)) return SupplierForecastExportDataService.getExportValue(business, columnName);

    const parsed = this.parseYearMonthFromColumnName(columnName);
    if (parsed && isColumnNamesOfExportFile(parsed.columnName)) {
      return SupplierForecastExportDataService.getExportValue(business, parsed.columnName, parsed.yearMonth);
    }
    return '';
  };

  /**
   * 合計行の必要な情報を取り出す
   * @param subTotalData - 合計行のデータ
   * @param columnName - 列名
   */
  static createSubTotalValueFetchFromProperty = (
    subTotalData: ISubTotalSupplierForecastExportData,
    columnName: string
  ): string | number => {
    if (isColumnNamesOfExportFile(columnName)) return SupplierForecastExportDataService.getExportValueSubTotal(subTotalData, columnName);

    const parsed = this.parseYearMonthFromColumnName(columnName);
    if (parsed && isColumnNamesOfExportFile(parsed.columnName)) {
      return SupplierForecastExportDataService.getExportValueSubTotal(subTotalData, parsed.columnName, parsed.yearMonth);
    }
    return '';
  };

  /**
   * エクセル出力を行うために、必要な情報を持つオブジェクトの作成を行う
   * @param businesses - 全取引情報
   * @param output - エクセル出力用のオブジェクト
   * @param rowInfo - 入力されたエクセルの行ごとの情報
   * @param workSheetInfo - worksheetの情報
   */
  static createAllTotalInfo = (
    businesses: SupplierForecastExportData[],
    output: ExcelExportResult,
    rowInfo: ExcelOutputRow,
    workSheetInfo: any
  ) => {
    // 列ごとに見ていく
    for (const [index, key] of Object.keys(rowInfo).entries()) {
      // 一応、セル番号が文字列かを見る
      if (typeof rowInfo[key].address === 'string') {
        // セル番号をコードで扱える形に変換する
        const cellAddress = XLSX.utils.decode_cell(rowInfo[key].address as string);
        // 取引の数分、下に行を進める
        const cell: CellAddress = { c: cellAddress.c, r: cellAddress.r + businesses.length };
        const writeToAddress = XLSX.utils.encode_cell(cell);
        const cellNumber = String(writeToAddress); // セル番号
        // セルのstyle情報の取得を行う
        // エクセル番号、セルに入れる値を持つオブジェクトの初期化
        if (!output[writeToAddress]) {
          output[writeToAddress] = {
            value: undefined,
            address: undefined,
            colorInfo: undefined,
            borderInfo: undefined,
            boldInfo: undefined,
          };
        }

        // 出力先セル番号の取得
        const propertyValue = this.createAllTotalValueFetchFromProperty(businesses, key);
        if (propertyValue !== '') {
          // 出力情報の登録
          output[cellNumber].address = writeToAddress;
          output[cellNumber].value = propertyValue;
          // style情報の登録を行う
          output[cellNumber].colorInfo = COLORS.GRAND_TOTAL;
          output[cellNumber].boldInfo = true;
          workSheetInfo.row(cell.r + 1).height(STYLES.GRAND_TOTAL_HEIGHT);

          const baseBorder = {
            top: { style: 'medium' as const, color: '000000' },
            bottom: { style: 'medium' as const, color: '000000' },
            left: true as const,
            right: true as const,
          };

          if (isColumnNamesOfExportFile(key) && key === '容量') {
            output[cellNumber].borderInfo = { ...baseBorder, left: { style: 'medium', color: '000000' } };
          } else if (index === Object.keys(rowInfo).length - 1) {
            output[cellNumber].borderInfo = { ...baseBorder, right: { style: 'medium', color: '000000' } };
          } else {
            output[cellNumber].borderInfo = baseBorder;
          }
        }
      }
    }
  };

  /**
   * 総計の必要な情報を取り出す
   * @param businesses - 全取引情報
   * @param columnName - 列名
   */
  static createAllTotalValueFetchFromProperty = (businesses: SupplierForecastExportData[], columnName: string): string | number => {
    if (isColumnNamesOfExportFile(columnName)) return SupplierForecastExportDataService.getExportValueAllTotal(businesses, columnName);

    const parsed = this.parseYearMonthFromColumnName(columnName);
    if (parsed && isColumnNamesOfExportFile(parsed.columnName)) {
      return SupplierForecastExportDataService.getExportValueAllTotal(businesses, parsed.columnName, parsed.yearMonth);
    }
    return '';
  };

  /**
   * 列名を取得する
   * @param type - 列タイプ
   * @returns 列名
   */
  private static getColumnName(type: string): ColumnNamesOfExportFile {
    return type === 'supplierForecast' ? '内示' : '販売実績';
  }

  /**
   * 列名から年月情報を抽出する
   * @param columnName - 列名
   * @returns 列名と年月情報
   */
  private static parseYearMonthFromColumnName(columnName: string): { columnName: string; yearMonth: IYearMonth } | null {
    if (!this.isAnnounceOrSalesResultProperty(columnName)) {
      return null;
    }
    const useColumnName = columnName.split('月')[1];
    const yearMonthString: string = columnName.split('月')[0];
    const yearMonth: IYearMonth = {
      year: Number(yearMonthString.split('_')[0]),
      month: Number(yearMonthString.split('_')[1]),
    };
    return { columnName: useColumnName, yearMonth };
  }

  /**
   * 「内示」または「販売実績」の列か判定する
   * @param key - 列名
   */
  static isAnnounceOrSalesResultProperty = (key: string): boolean => {
    return key.includes('月内示') || key.includes('月販売実績');
  };
}
